/**
 *
 * Module:        Component
 * Name:          Guru Chat Sidebar
 * Description:   Renders new-chat, search and chat history controls
 * Developers:    Adrian Engelmann, Kiran Jayaramu, Nicu Bratescu, Daniela Popescu
 * Copyright:     Copyright 2026 by Qalgo GmbH (www.qalgo.de). All rights reserved.
 *
 */

import React from 'react';
import {
	Box,
	CircularProgress,
	IconButton,
	Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {QeeColorButtonPrimary} from '../qeeColorButton';
import QeeTooltip from '../qeeTooltip';
import GuruChatList from './GuruChatList';
import {GURU_BODY_FONT_SIZE} from './guruTypography';

export default function GuruChatSidebar({chats, labels, loading, error, onOpenSearch, ...listProps}) {

	return (
		<Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper'}}>
			<Box sx={{p: 1.5, display: 'flex', gap: 0.75}}>
				<QeeColorButtonPrimary
					aria-label={labels.newChat}
					disabled={listProps.busy}
					onClick={listProps.onNewChat}
					sx={{
						flex: '0 0 auto',
						width: 'fit-content',
						mr: 0,
						fontSize: `${GURU_BODY_FONT_SIZE} !important`,
						lineHeight: 1.5,
						columnGap: 0.9,
						justifyContent: 'flex-start',
						borderRadius: 2,
						minHeight: 42,
						'&::before': {
							content: '""',
							display: 'block',
							flex: '0 0 21px',
							width: 21,
							height: 21,
							backgroundColor: 'currentColor',
							WebkitMaskImage: 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27black%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3E%3Cpath d=%27M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7%27/%3E%3Cpath d=%27M18.375 2.625a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z%27/%3E%3C/svg%3E")',
							WebkitMaskRepeat: 'no-repeat',
							WebkitMaskPosition: 'center',
							WebkitMaskSize: 'contain',
							maskImage: 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27black%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3E%3Cpath d=%27M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7%27/%3E%3Cpath d=%27M18.375 2.625a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z%27/%3E%3C/svg%3E")',
							maskRepeat: 'no-repeat',
							maskPosition: 'center',
							maskSize: 'contain'
						},
						'&:focus-visible': {
							outline: '2px solid var(--primary-color, #004c3f)',
							outlineOffset: 2
						}
					}}
				>
					{labels.newChat}
				</QeeColorButtonPrimary>
				<QeeTooltip title={(
					<Box component="span" sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
						<Box component="span">{labels.searchChats}</Box>
						<Box component="kbd" sx={{px: 0.65, py: 0.1, borderRadius: 0.75, bgcolor: 'rgba(255,255,255,0.18)', font: 'inherit'}}>
							Ctrl+K
						</Box>
					</Box>
				)}>
					<IconButton
						aria-label={labels.searchChats}
						onClick={onOpenSearch}
						sx={{
							ml: 'auto',
							flex: '0 0 42px',
							width: 42,
							height: 42,
							borderRadius: 2,
							color: 'var(--primary-color, #004c3f)',
							bgcolor: 'transparent',
							'&:hover': {bgcolor: 'action.hover'}
						}}
					>
						<SearchIcon />
					</IconButton>
				</QeeTooltip>
			</Box>
			<Box sx={{flex: '1 1 auto', minHeight: 0, overflowY: 'auto', pb: 1}}>
				{loading ? (
					<Box sx={{display: 'flex', justifyContent: 'center', py: 4}}><CircularProgress size={24} /></Box>
				) : error ? (
					<Typography color="error" sx={{px: 2, py: 2}}>{labels.historyLoadError}</Typography>
				) : (
					<GuruChatList chats={chats} labels={labels} {...listProps} />
				)}
			</Box>
		</Box>
	);
}
